import type { ReactNode } from 'react'
import type { StoredUsers } from '~/gql/users/users'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AccountStatusBadge } from '~/components/AccountStatusBadge'
import { Badge } from '~/components/ui/badge'
import { TableCell, TableRow } from '~/components/ui/table'
import { userDisplayName } from '~/helpers/users/display-name'
import { roleLabel } from '~/helpers/users/role-label'
import { lastConnectionLabel } from '~/pages/backoffice/admin/user/list/last-connection'

const empty = <span className="text-muted-foreground">—</span>

type IProps = {
  user: StoredUsers
  /** Sa propre ligne, mise en évidence. */
  isSelf: boolean
  /** Case d'affectation en masse ; absente sans le droit de l'utiliser. */
  selection?: { checked: boolean, onToggle: () => void }
  actions: ReactNode
}

/** Une ligne de la liste des comptes. */
function UserRow({ user, isSelf, selection, actions }: IProps) {
  const { t, i18n } = useTranslation()

  return (
    <TableRow className={isSelf ? 'bg-accent text-accent-foreground hover:bg-accent' : undefined}>
      {selection && (
        <TableCell>
          <input
            type="checkbox"
            checked={selection.checked}
            onChange={selection.onToggle}
            aria-label={userDisplayName(user)}
          />
        </TableCell>
      )}
      <TableCell>
        <Link to={`/back-office/users/${user.id}`} className="font-medium text-primary-text underline underline-offset-4">
          {user.email ?? user.username}
        </Link>
        {/* Sa propre ligne : dite en clair, pas seulement par le fond (RGAA 3.1). */}
        {isSelf && <span className="ml-1 text-muted-foreground">{t('users.you')}</span>}
      </TableCell>
      <TableCell>{user.firstName}</TableCell>
      <TableCell>{user.lastName}</TableCell>
      <TableCell>{user.company ?? empty}</TableCell>
      <TableCell>{user.department ?? empty}</TableCell>
      <TableCell>{roleLabel(t, user.role.name)}</TableCell>
      <TableCell>
        {user.groups?.length
          ? (
              <div className="flex flex-wrap gap-1">
                {user.groups.map(g => <Badge key={g.id} variant="secondary">{g.name}</Badge>)}
              </div>
            )
          : empty}
      </TableCell>
      <TableCell>{lastConnectionLabel(t, i18n.language, user)}</TableCell>
      <TableCell>
        <AccountStatusBadge status={user.status} />
      </TableCell>
      <TableCell className="text-right">{actions}</TableCell>
    </TableRow>
  )
}

export default UserRow
